import joi from 'joi';
import z from 'zod';

export enum ServiceLevel {
  PLATINUM = 'PLAT',
  GOLD = 'GOLD',
  SILVER = 'SLVR',
  BRONZE = 'BRNZ',
  INTERNAL = 'INTR',
  UNKNOWN = 'UNKN',
}

/**
 * This class is named TenantBodyNotifier in organization api.
 */
export interface UpsertAccountBody {
  organisationId: number;
  tenantId: number;
  name: string;
  languages: string[];
  externalId?: string;
  externalTenantId?: string;
  serviceLevel: ServiceLevel;
  moduleData: {
    website: {
      name: string;
    };
    country: string[];
    timezone: string;
    csm?: string;
    mrr?: string;
    phase?: string;
    industry?: string;
    engageTenantId?: number;
  };
}

export interface ArchiveOrRestoreAccountBody {
  externalTenantId: string;
}

export const $upsertAccountPayload = joi.object<UpsertAccountBody>({
  organisationId: joi.number(),
  tenantId: joi.number(),
  name: joi.string(),
  languages: joi.array().items(joi.string()).optional().allow(null),
  externalId: joi.string().optional().allow(null),
  externalTenantId: joi.string().optional().allow(null),
  moduleData: joi.object({
    timezone: joi.string(),
    engageTenantId: joi.number().optional(),
  }),
});

export const $archiveOrRestoreAccountPayload = joi.object<ArchiveOrRestoreAccountBody>({
  externalTenantId: joi.string().optional(),
});

export enum TenantModuleNotifierActions {
  CREATE = 'create_tenant',
  UPDATE = 'update_tenant',
  ARCHIVE = 'archive_tenant',
  RESTORE = 'restore_tenant',
}

export const $managerAccountPayload = joi.when('$action', {
  switch: [
    {
      is: TenantModuleNotifierActions.CREATE,
      then: $upsertAccountPayload.keys({
        moduleData: {
          timezone: joi.string(),
          engageTenantId: joi.number(), // not optional for create
        },
      }),
    },
    {
      is: TenantModuleNotifierActions.UPDATE,
      then: $upsertAccountPayload,
    },
    {
      is: TenantModuleNotifierActions.ARCHIVE,
      then: $archiveOrRestoreAccountPayload,
    },
    {
      is: TenantModuleNotifierActions.RESTORE,
      then: $archiveOrRestoreAccountPayload,
    },
  ],
  otherwise: joi.forbidden(),
});

export const $upsertAccountQueryParams = z.object({
  processUuid: z.string(),
  action: z.nativeEnum(TenantModuleNotifierActions),
});

export type ManageAccountQueryParams = z.infer<typeof $upsertAccountQueryParams>;
